import React, { createContext, useContext, useEffect, useMemo } from 'react';
import {
  buildLocalizedPath,
  DEFAULT_LOCALE,
  detectBrowserLocale,
  detectBrowserPreference,
  getPreferredLocale,
  getSavedLocale,
  LOCALE_STORAGE_KEY,
  normalizeLocale,
  parseLanguageTag,
  persistLocale,
  ROUTES,
  stripLocaleFromPath,
  SUPPORTED_LOCALES,
} from './config';
import en from './locales/en';
import ja from './locales/ja';
import ko from './locales/ko';
import ms from './locales/ms';
import th from './locales/th';
import tr from './locales/tr';

const translations = {
  en,
  ja,
  ko,
  ms,
  th,
  tr,
};

const I18nContext = createContext(null);

export function I18nProvider({ children, locale }) {
  const activeLocale = translations[locale] ? locale : DEFAULT_LOCALE;

  const value = useMemo(
    () => ({
      locale: activeLocale,
      messages: translations[activeLocale],
      localizePath: (path) => buildLocalizedPath(activeLocale, path),
    }),
    [activeLocale]
  );

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = activeLocale;
    }
  }, [activeLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);

  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider.');
  }

  return context;
}

export function usePageTitle(pageTitle) {
  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    document.title = pageTitle ? `${pageTitle} | 7Loves` : '7Loves';
  }, [pageTitle]);
}

export {
  buildLocalizedPath,
  DEFAULT_LOCALE,
  detectBrowserLocale,
  detectBrowserPreference,
  getPreferredLocale,
  getSavedLocale,
  LOCALE_STORAGE_KEY,
  normalizeLocale,
  parseLanguageTag,
  persistLocale,
  ROUTES,
  stripLocaleFromPath,
  SUPPORTED_LOCALES,
};
